"use client";

import type { TrafficChannel } from "@/lib/funnel-planner/schema";
import type { ChannelDef } from "@/lib/funnel-planner/channels";

type Props = {
  def: ChannelDef;
  channel: TrafficChannel | undefined;
  onToggle: (id: string) => void;
  recommended?: boolean;
  popular?: boolean;
  recommendedLabel?: string;
  popularLabel?: string;
  auditTicket?: string;
  showExpandToggle?: boolean;
  expanded?: boolean;
  expandControlsId?: string;
  expandLabel?: string;
  offerAssemblyCtaLabel?: string;
  offerAssemblyCurrentState?: "untouched_empty" | "active_inprogress" | "partial" | "complete_result" | "reduced_motion_static" | "failure_empty";
  offerAssemblyStatusLabel?: string;
};

export function ChannelCard({
  def,
  channel,
  onToggle,
  recommended,
  popular,
  recommendedLabel,
  popularLabel,
  auditTicket,
  showExpandToggle,
  expanded,
  expandControlsId,
  expandLabel,
  offerAssemblyCtaLabel,
  offerAssemblyCurrentState,
  offerAssemblyStatusLabel,
}: Props) {
  const active = channel != null;
  const badge = recommended ? recommendedLabel : popular ? popularLabel : null;
  const hasOfferAssemblyCta = Boolean(auditTicket);
  const ctaState = offerAssemblyCurrentState ?? (active ? "complete_result" : "active_inprogress");
  const ctaLabel = offerAssemblyCtaLabel ?? (active ? "Selected" : "Mark for review");
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-expanded={showExpandToggle ? Boolean(expanded) : undefined}
      aria-controls={showExpandToggle ? expandControlsId : undefined}
      aria-label={showExpandToggle && expandLabel ? `${def.label}. ${expandLabel}` : def.label}
      data-funnel-planner-offer-assembly-cta={hasOfferAssemblyCta ? "true" : undefined}
      data-funnel-planner-step-two-offer-assembly-cta={hasOfferAssemblyCta ? "true" : undefined}
      data-audit-ticket={auditTicket}
      data-tier-one-family={hasOfferAssemblyCta ? "offer_assembly_mini_cta_brief" : undefined}
      data-gamification-current-state={hasOfferAssemblyCta ? ctaState : undefined}
      data-gamification-reduced-motion-static={hasOfferAssemblyCta ? "supported" : undefined}
      data-gamification-supported-states={
        hasOfferAssemblyCta
          ? "untouched_empty active_inprogress partial complete_result reduced_motion_static failure_empty"
          : undefined
      }
      data-readiness-ready-count={hasOfferAssemblyCta ? (active ? 1 : 0) : undefined}
      data-readiness-total-count={hasOfferAssemblyCta ? 1 : undefined}
      data-readiness-tier={hasOfferAssemblyCta ? (active ? "brief_piece_ready" : "brief_piece_available") : undefined}
      data-blockers-bucket={hasOfferAssemblyCta ? (active ? "none" : "one") : undefined}
      onClick={() => onToggle(def.id)}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 8,
        borderRadius: 7,
        border: `1.5px solid ${active ? "#2563eb" : "#1e293b"}`,
        background: active ? "rgba(37,99,235,0.10)" : "rgba(255,255,255,0.03)",
        padding: "7px 10px",
        cursor: "pointer",
        width: "100%",
        textAlign: "start",
        transition: "border-color 0.12s, background 0.12s",
        minHeight: 44,
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.borderColor = "#334155";
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.borderColor = "#1e293b";
      }}
    >
      {badge && (
        <span data-funnel-planner-recommended-badge="true" style={{
          position: "absolute",
          top: -8,
          insetInlineEnd: 6,
          fontSize: 9,
          fontWeight: 700,
          lineHeight: 1,
          padding: "2px 5px",
          borderRadius: 4,
          background: recommended ? "#2563eb" : "#0e7490",
          color: "#e2e8f0",
          letterSpacing: "0.02em",
          pointerEvents: "none",
        }}>
          {badge}
        </span>
      )}
      <span style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 26,
        height: 20,
        borderRadius: 4,
        background: active ? "rgba(37,99,235,0.25)" : "rgba(255,255,255,0.06)",
        fontSize: 9,
        fontWeight: 700,
        color: active ? "#93c5fd" : "#64748b",
        padding: "0 3px",
        flexShrink: 0,
        letterSpacing: "-0.02em",
      }}>
        {def.icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: "block",
          fontSize: 12,
          fontWeight: 500,
          color: active ? "#93c5fd" : "#94a3b8",
          lineHeight: 1.3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
          {def.label}
        </span>
        {hasOfferAssemblyCta && (
          <>
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: 3,
                minHeight: 18,
                borderRadius: 999,
                border: active ? "1px solid rgba(16,185,129,0.45)" : "1px solid rgba(96,165,250,0.35)",
                background: active ? "rgba(16,185,129,0.12)" : "rgba(37,99,235,0.10)",
                color: active ? "#a7f3d0" : "#bfdbfe",
                fontSize: 9,
                fontWeight: 800,
                padding: "1px 6px",
                lineHeight: 1.1,
                whiteSpace: "nowrap",
              }}
            >
              {ctaLabel}
            </span>
            <span className="sr-only" role="status" aria-live="polite">
              {offerAssemblyStatusLabel ?? ctaLabel}
            </span>
          </>
        )}
      </span>
      {showExpandToggle ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
          style={{
            color: "#93c5fd",
            flexShrink: 0,
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.16s ease",
          }}
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      ) : active && (
        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 20 20" fill="#3b82f6" style={{ flexShrink: 0 }}>
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
    </button>
  );
}
